import { useCallback, useEffect, useMemo, useState } from 'react';

import axios from 'utils/axios';
import { useWorkspace } from 'contexts/WorkspaceContext';

export interface PortalNotificationItem {
  id: string;
  notificationId?: string;
  type: string;
  category?: string;
  title: string;
  summary: string;
  body?: string;
  createdAt?: string;
  updatedAt?: string;
  read: boolean;
  readAt?: string | null;
  sender?: {
    name: string;
    email?: string | null;
  } | null;
  attachments?: Array<{
    documentId?: string;
    name?: string;
    mime?: string;
    size?: number;
    url?: string;
  }>;
}

interface UsePortalNotificationsResult {
  notifications: PortalNotificationItem[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  markAsRead: (userNotificationId: string) => Promise<void>;
}

export default function usePortalNotifications(): UsePortalNotificationsResult {
  const { activeCommunityId } = useWorkspace();
  const [notifications, setNotifications] = useState<PortalNotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const feedUrl = useMemo(() => {
    const params = new URLSearchParams({ page: '1', pageSize: '50' });
    if (activeCommunityId) params.set('communityId', activeCommunityId);
    return `/api/notifications/portal/feed?${params.toString()}`;
  }, [activeCommunityId]);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await axios.get(feedUrl);
      const items = (res.data?.items || []) as PortalNotificationItem[];
      setNotifications(items);
      setUnreadCount(Number(res.data?.unreadCount || 0));
    } catch (err: any) {
      setError(err?.error?.message || err?.message || 'Failed to load notifications');
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [feedUrl]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      refresh();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const markAsRead = useCallback(async (userNotificationId: string) => {
    if (!userNotificationId) return;

    setNotifications((prev) =>
      prev.map((n) =>
        n.id === userNotificationId
          ? {
              ...n,
              read: true,
              readAt: n.readAt || new Date().toISOString(),
            }
          : n
      )
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      const res = await axios.post(`/api/notifications/portal/${userNotificationId}/read`);
      if (typeof res.data?.unreadCount === 'number') {
        setUnreadCount(res.data.unreadCount);
      }
    } catch (_err) {
      await refresh();
    }
  }, [refresh]);

  return { notifications, unreadCount, loading, error, refresh, markAsRead };
}
